import { useId, useMemo } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import Select, { components as selectComponents } from "react-select";
import AsyncSelect from "react-select/async";
import CreatableSelect from "react-select/creatable";
import AsyncCreatableSelect from "react-select/async-creatable";

/** @typedef {import("react-select").GroupBase<any>} FormSelectAnyGroup */
/** @typedef {import("react-select").Props<any, boolean, FormSelectAnyGroup>} FormSelectBaseProps */

/** Matches ClientOnBoarding Label + Input (client-onboarding-page) */
const LABEL_STYLE = {
  fontSize: "14px",
  fontWeight: "normal",
  color: "#333333",
  marginBottom: "5px",
  display: "block"
};

const BORDER_DEFAULT = "#e0e0e0";
const BORDER_INVALID = "#ff0000";
const PLACEHOLDER_COLOR = "#6c757d";

function ErrorIndicatorIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
      <circle cx="10" cy="10" r="9" fill="#ff0000" />
      <text
        x="10"
        y="14"
        textAnchor="middle"
        fill="#ffffff"
        fontSize="12"
        fontWeight="bold"
        fontFamily="system-ui, sans-serif"
      >
        !
      </text>
    </svg>
  );
}

function createSelectStyles(isInvalid) {
  return {
    control: (base, state) => ({
      ...base,
      minHeight: 38,
      borderRadius: 4,
      borderWidth: 1,
      borderStyle: "solid",
      borderColor: isInvalid ? BORDER_INVALID : BORDER_DEFAULT,
      boxShadow: "none",
      backgroundColor: "#ffffff",
      fontSize: 16,
      cursor: state.isDisabled ? "not-allowed" : "pointer"
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "6px 12px"
    }),
    placeholder: (base) => ({
      ...base,
      color: PLACEHOLDER_COLOR,
      fontSize: 16
    }),
    singleValue: (base) => ({
      ...base,
      color: "#333333",
      fontSize: 16
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: "#e7f1ff"
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: "#333333",
      fontSize: 14
    }),
    input: (base) => ({
      ...base,
      color: "#333333",
      fontSize: 16
    }),
    indicatorSeparator: () => ({
      display: "none"
    }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: "0 10px",
      color: isInvalid ? BORDER_INVALID : "#6c757d"
    }),
    clearIndicator: (base) => ({
      ...base,
      color: "#6c757d"
    }),
    menu: (base) => ({
      ...base,
      borderRadius: 4,
      border: `1px solid ${BORDER_DEFAULT}`,
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
      zIndex: 9999,
      overflow: "hidden"
    }),
    menuList: (base) => ({
      ...base,
      padding: 0
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999
    }),
    option: (base, state) => ({
      ...base,
      fontSize: 16,
      cursor: "pointer",
      backgroundColor: state.isSelected
        ? "#e7f1ff"
        : state.isFocused
          ? "#f8f9fa"
          : "#ffffff",
      color: "#333333"
    })
  };
}

function mergeStyles(defaultStyles, userStyles) {
  if (!userStyles || typeof userStyles !== "object") return defaultStyles;
  const keys = new Set([...Object.keys(defaultStyles), ...Object.keys(userStyles)]);
  const merged = {};
  keys.forEach((key) => {
    const defFn = defaultStyles[key];
    const userFn = userStyles[key];
    merged[key] = (base, state) => {
      let next = base;
      if (defFn) next = defFn(next, state);
      if (userFn) next = userFn(next, state);
      return next;
    };
  });
  return merged;
}

/**
 * react-select / AsyncSelect with label and error message, styled like ClientOnBoarding inputs.
 * Use `async` + `loadOptions` for async search. Pass Formik-style `invalid` + `error` for validation UI.
 *
 * @param {object} props
 * @param {import("react").ReactNode} props.label
 * @param {string} [props.error] Shown when `invalid`
 * @param {boolean} [props.invalid]
 * @param {boolean} [props.async] When true, renders AsyncSelect (`loadOptions` etc.)
 * @param {boolean} [props.creatable] Allow custom values: AsyncCreatableSelect or CreatableSelect
 * @param {string} [props.className] Wrapper `div`
 * @param {string} [props.inputId] Linked to label; autogenerated if omitted
 * @param {HTMLElement | null | undefined} [props.menuPortalTarget] Default `document.body`
 * @param {FormSelectBaseProps["styles"]} [props.styles] Merged with built-in styles
 * @param {FormSelectBaseProps["components"]} [props.components] Merged unless `DropdownIndicator` is overridden
 * Commonly used Select / AsyncSelect props (see react-select docs; additional keys still work via rest spread).
 * @param {FormSelectBaseProps["value"]} [props.value]
 * @param {FormSelectBaseProps["onChange"]} [props.onChange]
 * @param {FormSelectBaseProps["onBlur"]} [props.onBlur]
 * @param {FormSelectBaseProps["onFocus"]} [props.onFocus]
 * @param {FormSelectBaseProps["options"]} [props.options]
 * @param {FormSelectBaseProps["placeholder"]} [props.placeholder]
 * @param {FormSelectBaseProps["defaultValue"]} [props.defaultValue]
 * @param {FormSelectBaseProps["isMulti"]} [props.isMulti]
 * @param {FormSelectBaseProps["isClearable"]} [props.isClearable]
 * @param {FormSelectBaseProps["isDisabled"]} [props.isDisabled]
 * @param {FormSelectBaseProps["isSearchable"]} [props.isSearchable]
 * @param {FormSelectBaseProps["isLoading"]} [props.isLoading]
 * @param {FormSelectBaseProps["name"]} [props.name]
 * @param {FormSelectBaseProps["menuIsOpen"]} [props.menuIsOpen]
 * @param {FormSelectBaseProps["onMenuOpen"]} [props.onMenuOpen]
 * @param {FormSelectBaseProps["onMenuClose"]} [props.onMenuClose]
 * @param {FormSelectBaseProps["filterOption"]} [props.filterOption]
 * @param {FormSelectBaseProps["formatOptionLabel"]} [props.formatOptionLabel]
 * @param {FormSelectBaseProps["getOptionLabel"]} [props.getOptionLabel]
 * @param {FormSelectBaseProps["getOptionValue"]} [props.getOptionValue]
 * @param {FormSelectBaseProps["noOptionsMessage"]} [props.noOptionsMessage]
 * @param {FormSelectBaseProps["loadingMessage"]} [props.loadingMessage]
 * @param {FormSelectBaseProps["onInputChange"]} [props.onInputChange]
 * @param {FormSelectBaseProps["blurInputOnSelect"]} [props.blurInputOnSelect]
 * @param {FormSelectBaseProps["closeMenuOnSelect"]} [props.closeMenuOnSelect]
 * @param {FormSelectBaseProps["hideSelectedOptions"]} [props.hideSelectedOptions]
 * @param {FormSelectBaseProps["isOptionDisabled"]} [props.isOptionDisabled]
 * @param {FormSelectBaseProps["autoFocus"]} [props.autoFocus]
 * @param {FormSelectBaseProps["instanceId"]} [props.instanceId]
 * @param {FormSelectBaseProps["tabIndex"]} [props.tabIndex]
 * @param {FormSelectBaseProps["maxMenuHeight"]} [props.maxMenuHeight]
 * @param {FormSelectBaseProps["minMenuHeight"]} [props.minMenuHeight]
 * @param {FormSelectBaseProps["menuPlacement"]} [props.menuPlacement]
 * @param {(inputValue: string, callback: (options: import("react-select").OptionsOrGroups<any, FormSelectAnyGroup>) => void) => void | Promise<import("react-select").OptionsOrGroups<any, FormSelectAnyGroup>>} [props.loadOptions] AsyncSelect
 * @param {boolean | ReadonlyArray<any>} [props.defaultOptions] AsyncSelect
 * @param {boolean} [props.cacheOptions] AsyncSelect
 * @param {FormSelectBaseProps["id"]} [props.id]
 * @param {FormSelectBaseProps["required"]} [props.required]
 * @param {FormSelectBaseProps["form"]} [props.form]
 * @param {string} [props.selectClassName] Extra classes on the react-select root (merged with internal classes). You can also pass `className` inside advanced spread-only props.
 * @param {FormSelectBaseProps["formatCreateLabel"]} [props.formatCreateLabel] creatable
 * @param {FormSelectBaseProps["isValidNewOption"]} [props.isValidNewOption] creatable
 * @param {FormSelectBaseProps["onCreateOption"]} [props.onCreateOption] creatable
 * @param {FormSelectBaseProps["getNewOptionData"]} [props.getNewOptionData] creatable
 * @param {FormSelectBaseProps["createOptionPosition"]} [props.createOptionPosition] creatable
 * @param {boolean} [props.allowCreateWhileLoading] creatable + async
 */
function FormSelectField({
  label,
  error,
  invalid = false,
  async: isAsync = false,
  creatable = false,
  className,
  selectClassName,
  inputId: inputIdProp,
  menuPortalTarget,
  styles: userStyles,
  components: userComponents,
  value,
  onChange,
  onBlur,
  onFocus,
  options,
  placeholder,
  defaultValue,
  isMulti,
  isClearable,
  isDisabled,
  isSearchable,
  isLoading,
  name,
  menuIsOpen,
  onMenuOpen,
  onMenuClose,
  filterOption,
  formatOptionLabel,
  getOptionLabel,
  getOptionValue,
  noOptionsMessage,
  loadingMessage,
  onInputChange,
  blurInputOnSelect,
  closeMenuOnSelect,
  hideSelectedOptions,
  isOptionDisabled,
  autoFocus,
  instanceId,
  tabIndex,
  maxMenuHeight,
  minMenuHeight,
  menuPlacement,
  loadOptions,
  defaultOptions,
  cacheOptions,
  id,
  required,
  form,
  formatCreateLabel,
  isValidNewOption,
  onCreateOption,
  getNewOptionData,
  createOptionPosition,
  allowCreateWhileLoading,
  ...selectProps
}) {
  const reactId = useId();
  const inputId = inputIdProp || `form-select-${reactId.replace(/:/g, "")}`;

  const hasError = Boolean(invalid);
  const errorMessage = typeof error === "string" ? error : "";

  const defaultStyles = useMemo(() => createSelectStyles(hasError), [hasError]);
  const styles = useMemo(
    () => mergeStyles(defaultStyles, userStyles),
    [defaultStyles, userStyles]
  );

  const mergedComponents = useMemo(() => {
    if (userComponents?.DropdownIndicator) {
      return userComponents;
    }
    const DropdownIndicator = (props) => (
      <selectComponents.DropdownIndicator {...props}>
        {hasError ? (
          <span style={{ display: "flex", alignItems: "center" }}>
            <ErrorIndicatorIcon />
          </span>
        ) : (
          props.children
        )}
      </selectComponents.DropdownIndicator>
    );
    DropdownIndicator.propTypes = {
      children: PropTypes.node
    };
    return {
      ...userComponents,
      DropdownIndicator
    };
  }, [hasError, userComponents]);

  const { className: selectClassNameFromRest, ...restSelectProps } = selectProps;

  const portalTarget =
    menuPortalTarget !== undefined
      ? menuPortalTarget
      : typeof document !== "undefined"
        ? document.body
        : null;

  const commonProps = {
    ...restSelectProps,
    value,
    onChange,
    onBlur,
    onFocus,
    options,
    placeholder,
    defaultValue,
    isMulti,
    isClearable,
    isDisabled,
    isSearchable,
    isLoading,
    name,
    menuIsOpen,
    onMenuOpen,
    onMenuClose,
    filterOption,
    formatOptionLabel,
    getOptionLabel,
    getOptionValue,
    noOptionsMessage,
    loadingMessage,
    onInputChange,
    blurInputOnSelect,
    closeMenuOnSelect,
    hideSelectedOptions,
    isOptionDisabled,
    autoFocus,
    instanceId,
    tabIndex,
    maxMenuHeight,
    minMenuHeight,
    menuPlacement,
    loadOptions,
    defaultOptions,
    cacheOptions,
    id,
    required,
    form,
    formatCreateLabel,
    isValidNewOption,
    onCreateOption,
    getNewOptionData,
    createOptionPosition,
    allowCreateWhileLoading,
    inputId,
    className: classNames(
      "react-select-container",
      "form-select-field",
      hasError && "is-invalid",
      selectClassName,
      selectClassNameFromRest
    ),
    classNamePrefix: "jobarkFsf",
    styles,
    components: mergedComponents,
    menuPortalTarget: portalTarget,
    menuPosition: "fixed"
  };

  return (
    <div className={classNames("form-select-field-wrap", className)}>
      <label htmlFor={inputId} style={LABEL_STYLE}>
        {label}
      </label>
      {(() => {
        if (isAsync && creatable) return <AsyncCreatableSelect {...commonProps} />;
        if (isAsync) return <AsyncSelect {...commonProps} />;
        if (creatable) return <CreatableSelect {...commonProps} />;
        return <Select {...commonProps} />;
      })()}
      {hasError && errorMessage ? (
        <div className="invalid-feedback d-block">{errorMessage}</div>
      ) : null}
    </div>
  );
}

FormSelectField.propTypes = {
  label: PropTypes.node.isRequired,
  error: PropTypes.string,
  invalid: PropTypes.bool,
  async: PropTypes.bool,
  creatable: PropTypes.bool,
  className: PropTypes.string,
  selectClassName: PropTypes.string,
  inputId: PropTypes.string,
  menuPortalTarget: PropTypes.oneOfType([PropTypes.any]),
  styles: PropTypes.object,
  components: PropTypes.object,
  value: PropTypes.any,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  options: PropTypes.array,
  placeholder: PropTypes.node,
  defaultValue: PropTypes.any,
  isMulti: PropTypes.bool,
  isClearable: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isSearchable: PropTypes.bool,
  isLoading: PropTypes.bool,
  name: PropTypes.string,
  menuIsOpen: PropTypes.bool,
  onMenuOpen: PropTypes.func,
  onMenuClose: PropTypes.func,
  filterOption: PropTypes.func,
  formatOptionLabel: PropTypes.func,
  getOptionLabel: PropTypes.func,
  getOptionValue: PropTypes.func,
  noOptionsMessage: PropTypes.func,
  loadingMessage: PropTypes.func,
  onInputChange: PropTypes.func,
  blurInputOnSelect: PropTypes.bool,
  closeMenuOnSelect: PropTypes.bool,
  hideSelectedOptions: PropTypes.bool,
  isOptionDisabled: PropTypes.func,
  autoFocus: PropTypes.bool,
  instanceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  tabIndex: PropTypes.number,
  maxMenuHeight: PropTypes.number,
  minMenuHeight: PropTypes.number,
  menuPlacement: PropTypes.string,
  loadOptions: PropTypes.func,
  defaultOptions: PropTypes.oneOfType([PropTypes.bool, PropTypes.array]),
  cacheOptions: PropTypes.bool,
  id: PropTypes.string,
  required: PropTypes.bool,
  form: PropTypes.string,
  formatCreateLabel: PropTypes.func,
  isValidNewOption: PropTypes.func,
  onCreateOption: PropTypes.func,
  getNewOptionData: PropTypes.func,
  createOptionPosition: PropTypes.string,
  allowCreateWhileLoading: PropTypes.bool
};

FormSelectField.defaultProps = {
  error: "",
  invalid: false,
  async: false,
  creatable: false
};

export default FormSelectField;
